import { useUser } from '../context/UserContext';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminPanel = () => {
    const { user, authFetch } = useUser();
    const navigate = useNavigate();

    const [usersList, setUsersList] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [errorUsers, setErrorUsers] = useState(null);
    const [updatingRoleId, setUpdatingRoleId] = useState(null);
    const [updateRoleError, setUpdateRoleError] = useState(null);

    useEffect(() => {
        if (!user) {
            navigate('/auth');
        }
    }, [user, navigate]);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoadingUsers(true);
            setErrorUsers(null);
            try {
                const data = await authFetch('/users');
                setUsersList(data || []);
            } catch (error) {
                console.error("Failed to fetch users:", error);
                setErrorUsers('Не удалось загрузить список пользователей.');
            } finally {
                setLoadingUsers(false);
            }
        };
        fetchUsers();
    }, [authFetch]);

    const handleRoleChange = async (userId, newRole) => {
        if (!userId || !newRole) return;

        setUpdatingRoleId(userId);
        setUpdateRoleError(null);

        try {
            await authFetch(`/users/${userId}/role`, {
                method: 'PATCH',
                body: JSON.stringify({ role: newRole }),
            });

            setUsersList(currentUsers => 
                currentUsers.map(u => 
                    u.id === userId ? { ...u, role: newRole } : u
                )
            );
        } catch (error) {
            console.error("Failed to update role:", error);
            setUpdateRoleError(`Не удалось изменить роль: ${error.message || 'ошибка сервера'}`);
        } finally {
            setUpdatingRoleId(null);
        }
    };

    const isLoading = loadingUsers;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center text-white p-8">
                Загрузка пользователей...
            </div>
        );
    }

    return (
        <div className="text-gray-200 p-4 md:p-8">
          <div className="w-full max-w-[95vw] mx-auto p-4 md:p-6 space-y-8 bg-gray-800 bg-opacity-80 backdrop-filter backdrop-blur-sm rounded-xl shadow-2xl">

            <h2 className="text-2xl font-semibold mb-6 text-white">Управление пользователями</h2>
            {updateRoleError && <p className="text-red-400 bg-red-900 bg-opacity-50 p-3 rounded-md text-center mb-4">{updateRoleError}</p>}
            {errorUsers && !loadingUsers && <p className="text-yellow-400 bg-yellow-900 bg-opacity-50 p-3 rounded-md text-center mb-4">{errorUsers}</p>}
      
            <div className="overflow-x-auto bg-gray-800 rounded-lg shadow-md">
              <table className="min-w-full table-auto text-left text-sm font-light text-gray-300">
                <thead className="bg-gray-700 border-b border-gray-600 text-xs text-gray-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3">ID</th>
                    <th className="px-6 py-3">Username</th>
                    <th className="px-6 py-3">Имя</th>
                    <th className="px-6 py-3">Фамилия</th>
                    <th className="px-6 py-3">Дата создания</th>
                    <th className="px-6 py-3">Роль</th>
                    <th className="px-6 py-3">Баланс</th>
                    <th className="px-6 py-3">Изменить баланс</th>
                    <th className="px-6 py-3">Статус</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {usersList.length > 0 ? (
                    usersList.map((usr, index) => (
                      <tr
                        key={usr.id || usr.telegramId}
                        className={`transition duration-150 ease-in-out ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-800 bg-opacity-50'} hover:bg-gray-700 ${updatingRoleId === usr.id ? 'opacity-60 animate-pulse' : ''}`}
                      >
                        <td className="whitespace-nowrap px-6 py-4 text-gray-400 font-mono text-xs">{usr.id || usr.telegramId}</td>
                        <td className={`whitespace-nowrap px-6 py-4 font-medium ${usr.isBlocked ? 'text-red-400 line-through' : 'text-white'}`}>{usr.username}</td>
                        <td className="whitespace-nowrap px-6 py-4">{usr.firstName || '-'}</td>
                        <td className="whitespace-nowrap px-6 py-4">{usr.lastName || '-'}</td>
                        <td className="whitespace-nowrap px-6 py-4">{usr.createdAt ? new Date(usr.createdAt).toLocaleDateString() : '-'}</td>
                        <td className="whitespace-nowrap px-6 py-4 font-medium">
                          <select
                            value={usr.role}
                            onChange={(e) => handleRoleChange(usr.id, e.target.value)}
                            disabled={updatingRoleId === usr.id}
                            className="w-full max-w-[120px] bg-gray-700 border border-gray-600 text-white text-xs rounded-md p-1.5"
                          >
                            <option value="user">Пользователь</option>
                            <option value="admin">Админ</option>
                            <option value="customer">Поставщик</option>
                          </select>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">{usr.balance ?? 0} ₽</td>
                        <td className="whitespace-nowrap px-6 py-4 flex flex-col gap-2 items-start">
                          {/* + Добавить */}
                          <div className="flex gap-2 items-center">
                            <input
                              type="number"
                              className="bg-gray-700 border border-gray-600 rounded-md text-white px-2 py-1 w-24 text-sm"
                              placeholder="Добавить"
                              value={usr.addAmount || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                setUsersList((prev) =>
                                  prev.map((u) => (u.id === usr.id ? { ...u, addAmount: value } : u))
                                );
                              }}
                            />
                            <button
                              onClick={async () => {
                                try {
                                  const response = await authFetch(`/users/${usr.id}/balance`, {
                                    method: 'PATCH',
                                    body: JSON.stringify({ amount: Number(usr.addAmount) }),
                                  });
                                  const result = await response.json();
                                  setUsersList((prev) =>
                                    prev.map((u) => (u.id === usr.id ? { ...u, balance: result.balance, addAmount: '' } : u))
                                  );
                                } catch (error) {
                                  alert('Ошибка при добавлении баланса');
                                  console.error(error);
                                }
                              }}
                              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded-md"
                            >
                              Добавить
                            </button>
                          </div>
      
                          {/* - Убрать */}
                          <div className="flex gap-2 items-center">
                            <input
                              type="number"
                              className="bg-gray-700 border border-gray-600 rounded-md text-white px-2 py-1 w-24 text-sm"
                              placeholder="Убрать"
                              value={usr.removeAmount || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                setUsersList((prev) =>
                                  prev.map((u) => (u.id === usr.id ? { ...u, removeAmount: value } : u))
                                );
                              }}
                            />
                            <button
                              onClick={async () => {
                                try {
                                  const response = await authFetch(`/users/${usr.id}/balance`, {
                                    method: 'PATCH',
                                    body: JSON.stringify({ amount: -Math.abs(Number(usr.removeAmount)) }),
                                  });
                                  const result = await response.json();
                                  setUsersList((prev) =>
                                    prev.map((u) => (u.id === usr.id ? { ...u, balance: result.balance, removeAmount: '' } : u))
                                  );
                                } catch (error) {
                                  alert('Баланс уменьшен');
                                  console.error(error);
                                }
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 rounded-md"
                            >
                              Убрать
                            </button>
                          </div>
      
                          {/* ✅ Блокировка */}
                          <div className="mt-2">
                            <button
                              onClick={async () => {
                                try {
                                  const response = await authFetch(`/users/${usr.id}/block`, {
                                    method: 'PATCH',
                                    body: JSON.stringify({ block: !usr.isBlocked }),
                                  });
                                  const result = await response.json();
                                  setUsersList((prev) =>
                                    prev.map((u) => (u.id === usr.id ? { ...u, isBlocked: result.isBlocked } : u))
                                  );
                                } catch (error) {
                                  alert('пользователь заблокирован');
                                  console.error(error);
                                }
                              }}
                              className={`text-xs px-3 py-1 rounded-md font-medium ${
                                usr.isBlocked
                                  ? 'bg-green-600 hover:bg-green-700 text-white'
                                  : 'bg-yellow-600 hover:bg-yellow-700 text-black'
                              }`}
                            >
                              {usr.isBlocked ? 'Разблокировать' : 'Заблокировать'}
                            </button>
                          </div>
                        </td>
      
                        {/* 🧩 Статус */}
                        <td className="whitespace-nowrap px-6 py-4">
                          {usr.isBlocked ? (
                            <span className="text-red-500 font-semibold">Заблокирован</span>
                          ) : (
                            <span className="text-green-400">Активен</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-6 text-gray-500 italic">
                        {loadingUsers ? 'Загрузка пользователей...' : 'Пользователи не найдены.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      );
      
};

export default AdminPanel;
